import moment from 'moment';
import React from 'react';
import { useQuery } from '@apollo/client';
import { MenuSeparator, MenuSimpleItem } from '../components/Menu';
import { gql } from '../graphql/generated/gql';
import { BillingSummaryStatus, PlanTier } from '../graphql/generated/graphql';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import Link from '../utils/router/Link';
import * as styles from './TeamUpgradeInfo.module.scss';
import TrialProgressCircle from './TrialProgressCircle';

const TEAM_UPGRADE_INFO_QUERY = gql(/** @complexity 6 */ `
  query teamUpgradeInfo {
    currentSession {
      organization {
        id
        billingStatus
        planTier
        insertedAt
        trialEndAt
      }
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    billingSummary {
      seatCount
      seatLimit
    }
  }
`);

const TeamUpgradeInfo = () => {
  const { data } = useQuery(TEAM_UPGRADE_INFO_QUERY);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const organization = data?.currentSession?.organization;

  if (!data || !organization) {
    return null;
  }

  const { billingSummary } = data;

  const today = moment().startOf('day');
  const trialEndDay = moment(organization.trialEndAt).startOf('day');
  const insertDay = moment(organization.insertedAt).startOf('day');
  const remainingDays = trialEndDay.diff(today, 'days');
  const trialLength = trialEndDay.diff(insertDay, 'days');

  const remainingSeats = Math.max(
    (billingSummary.seatLimit ?? 0) - (billingSummary.seatCount ?? 0),
    0,
  );

  const upgradeLink = !isMobile && (
    <Link className={styles.upgradeButton} to="/admin/billing">
      Upgrade now
    </Link>
  );

  return (
    <>
      {organization.billingStatus === BillingSummaryStatus.Trialing &&
        !!organization.trialEndAt && (
          <>
            <MenuSeparator fullWidth />
            <MenuSimpleItem>
              <div className={styles.trialInfo}>
                {remainingDays > 0 ? (
                  <>
                    <div className={styles.progressContainer}>
                      <TrialProgressCircle
                        remaining={remainingDays}
                        total={trialLength}
                      />
                    </div>
                    <div>
                      Your team is on a trial with {remainingDays} days left.{' '}
                      {upgradeLink}
                    </div>
                  </>
                ) : (
                  <div>
                    Your trial has expired. Upgrade to enable editing.{' '}
                    {upgradeLink}
                  </div>
                )}
              </div>
            </MenuSimpleItem>
          </>
        )}

      {organization.planTier === PlanTier.Free && (
        <>
          <MenuSeparator fullWidth />
          <MenuSimpleItem>
            <div className={styles.trialInfo}>
              <div className={styles.progressContainer}>
                <TrialProgressCircle
                  remaining={remainingSeats}
                  total={data.billingSummary.seatLimit ?? 0}
                />
              </div>
              <div>
                Your team is on the free plan with {remainingSeats} invites
                left. {upgradeLink}
              </div>
            </div>
          </MenuSimpleItem>
        </>
      )}
    </>
  );
};

export default TeamUpgradeInfo;
